/**
 * TwoFactorCard (PRE_LAUNCH H11 / F7).
 *
 * Settings tile to enrol / disable TOTP-based 2FA. Three states:
 *   - off + setup not started: "Enable 2FA" button → calls POST /auth/2fa/setup
 *   - off + setup in progress: QR + 6-digit input → POST /auth/2fa/confirm
 *   - on:                      "Disable 2FA" (requires current password)
 *
 * On successful enrol, the dialog reveals 8 one-time recovery codes ONCE.
 * The user must download / print them before closing — the codes are
 * never shown again.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, Alert, Card, CardContent, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import api from '../../services/api';

interface Props {
  /** Initial flag from /user/profile or login response. Optional — the
   *  card refetches on mount so the parent doesn't need to plumb it. */
  initialEnabled?: boolean;
  /** Called after a successful enable/disable so the parent can refresh. */
  onStateChange?: (enabled: boolean) => void;
}

const TwoFactorCard: React.FC<Props> = ({ initialEnabled = false, onStateChange }) => {
  const [enabled, setEnabled] = useState(initialEnabled);

  // Fetch the authoritative flag on mount so the card reflects current state
  // even if the parent didn't pass initialEnabled. Errors are non-fatal —
  // the default `false` produces a safe "Enable 2FA" CTA.
  useEffect(() => {
    let cancelled = false;
    api.get('/user/profile').then((resp) => {
      if (cancelled) return;
      const totp = (resp.data as any)?.totpEnabled;
      if (typeof totp === 'boolean') setEnabled(totp);
    }).catch(() => { /* swallow */ });
    return () => { cancelled = true; };
  }, []);
  const [stage, setStage] = useState<'idle' | 'enrolling' | 'recoveryCodes' | 'disabling'>('idle');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [disablePwd, setDisablePwd] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setError(null); setBusy(true);
    try {
      const resp = await api.post('/auth/2fa/setup');
      const data = resp.data as any;
      setQrDataUrl(data.qrDataUrl);
      setSecret(data.secret);
      setStage('enrolling');
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Could not start 2FA setup');
    } finally { setBusy(false); }
  };

  const confirmCode = async () => {
    if (!/^\d{6}$/.test(code)) { setError('Enter the 6-digit code from your authenticator app'); return; }
    setError(null); setBusy(true);
    try {
      const resp = await api.post('/auth/2fa/confirm', { code });
      const data = resp.data as any;
      setRecoveryCodes(data.recoveryCodes || []);
      setEnabled(true);
      setStage('recoveryCodes');
      onStateChange?.(true);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Code did not match');
    } finally { setBusy(false); }
  };

  const doDisable = async () => {
    if (!disablePwd) { setError('Current password required'); return; }
    setError(null); setBusy(true);
    try {
      await api.post('/auth/2fa/disable', { currentPassword: disablePwd });
      setEnabled(false);
      setStage('idle');
      setDisablePwd('');
      onStateChange?.(false);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Could not disable 2FA');
    } finally { setBusy(false); }
  };

  const closeRecovery = () => {
    setRecoveryCodes(null);
    setQrDataUrl(null);
    setSecret(null);
    setCode('');
    setStage('idle');
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <SecurityIcon color={enabled ? 'success' : 'action'} />
          <Typography variant="h6">Two-Factor Authentication</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Protect your account with a time-based one-time code from an authenticator app
          (Google Authenticator, Authy, 1Password, Microsoft Authenticator).
          <strong> Recommended for live trading.</strong>
        </Typography>

        {enabled ? (
          <Alert severity="success" sx={{ mb: 2 }}>2FA is enabled on this account.</Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>2FA is not enabled.</Alert>
        )}

        {stage === 'idle' && !enabled && (
          <Button variant="contained" onClick={startSetup} disabled={busy}>
            {busy ? 'Setting up…' : 'Enable 2FA'}
          </Button>
        )}

        {stage === 'idle' && enabled && (
          <Button variant="outlined" color="error" onClick={() => setStage('disabling')}>
            Disable 2FA
          </Button>
        )}

        {stage === 'enrolling' && qrDataUrl && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              1) Scan the QR with your authenticator app:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img src={qrDataUrl} alt="2FA setup QR code" style={{ width: 200, height: 200 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Can't scan? Enter this secret manually: <code style={{ fontFamily: 'monospace' }}>{secret}</code>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              2) Enter the 6-digit code your app shows:
            </Typography>
            <TextField
              fullWidth size="small" label="6-digit code" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              sx={{ mb: 2 }}
            />
            <Button variant="contained" onClick={confirmCode} disabled={busy || code.length !== 6}>
              {busy ? 'Verifying…' : 'Confirm & Enable'}
            </Button>{' '}
            <Button onClick={() => setStage('idle')} disabled={busy}>Cancel</Button>
          </Box>
        )}

        {stage === 'disabling' && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              To disable 2FA, confirm your current password:
            </Typography>
            <TextField
              fullWidth size="small" type="password" label="Current password"
              value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button variant="contained" color="error" onClick={doDisable} disabled={busy}>
              {busy ? 'Disabling…' : 'Disable 2FA'}
            </Button>{' '}
            <Button onClick={() => { setStage('idle'); setDisablePwd(''); setError(null); }} disabled={busy}>Cancel</Button>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </CardContent>

      <Dialog open={stage === 'recoveryCodes'} onClose={closeRecovery} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogTitle>Save Your Recovery Codes</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>These codes are shown ONCE.</strong> Save them somewhere safe. Each
            code works as a one-time replacement for your authenticator if you ever
            lose your phone.
          </Alert>
          <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1, fontFamily: 'monospace' }}>
            {(recoveryCodes || []).map((c) => (
              <ListItem key={c} sx={{ py: 0.3, fontFamily: 'monospace', fontSize: '1rem' }}>
                {c}
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            const blob = new Blob([(recoveryCodes || []).join('\n')], { type: 'text/plain' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'earngush-2fa-recovery-codes.txt';
            a.click();
            URL.revokeObjectURL(a.href);
          }}>Download .txt</Button>
          <Button variant="contained" onClick={closeRecovery}>
            I have saved them — close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TwoFactorCard;
